import axios from 'axios';
import { recordRequest } from '../utils/perfLog'; // TEMPORARY — see perfLog.js

// Milliseconds the server says it spent on this answer (see the X-Memo-Ms
// middleware). Lets the report separate "we are slow" from "the line is slow".
function serverMsOf(res) {
  const v = res?.headers?.['x-memo-ms'];
  const n = v == null ? NaN : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:10000',
  withCredentials: true, // send httpOnly cookie automatically on every request
});

// Auth is the httpOnly first-party cookie on .memocal.app, sent automatically
// (withCredentials above). That covers every browser.
//
// It cannot cover the installed Android app. That app is a WebView on
// memocal.app, Google refuses OAuth inside an embedded WebView, so the sign-in
// runs in the phone's real browser — and a browser's cookie jar is not the
// WebView's. The cookie was landing in Chrome and the app never saw it, which
// is why it asked for a fresh sign-in on every single launch.
//
// So the app keeps a token of its own instead, collected once at sign-in (see
// utils/appSession.js) and sent here. Note the ORDER: the cookie wins wherever
// there is one, and this header only fills the gap. Nothing changes for anybody
// using a browser — with no stored token, no header is added at all.
const APP_TOKEN_KEY = 'memo_app_token_v1';

/** The installed app's own session token, or null. Never throws. */
export function getAppToken() {
  try { return localStorage.getItem(APP_TOKEN_KEY) || null; } catch (_) { return null; }
}
export function setAppToken(token) {
  try {
    if (token) localStorage.setItem(APP_TOKEN_KEY, token);
    else localStorage.removeItem(APP_TOKEN_KEY);
  } catch (_) { /* private mode — the cookie path still works */ }
}

apiClient.interceptors.request.use((config) => {
  const t = getAppToken();
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ── Reads: never ask the same thing twice at once ────────────────────────────
// Opening Today fires half a dozen requests, and three different parts of the
// app (the week view, the alarm scheduler, the native reminder sync) each ask
// for the SAME week independently. Every one of those costs a round trip to
// the server and, from there, to Google Calendar — which is what made moving
// around the app feel slow, far more than the server itself (~150 ms).
//
// So, for GET only:
//   • the same request already in flight is shared instead of repeated;
//   • an answer received moments ago is reused instead of asked again.
// Anything that changes data clears this (see invalidateEventCache), so a
// saved event is never hidden behind a stale copy.
// How long an answer stays good enough to reuse without asking again.
// Raised from 25s: 25 seconds is shorter than it takes to look at Today, open
// an event, read it and come back, so the trip back paid for a fresh round of
// requests and a fresh spinner every time. Anything you change yourself clears
// this cache immediately (see below), so the only thing that can be a minute
// out of date is a change made somewhere else — another device, or another
// member of a shared calendar.
const FRESH_MS = 90000;
const inFlight = new Map();   // key → pending promise
const recent = new Map();     // key → { at, res }
const rawGet = apiClient.get.bind(apiClient);

function keyOf(url, config) {
  return url + '|' + JSON.stringify(config?.params || {});
}

apiClient.get = function cachingGet(url, config) {
  // Downloads (the .ics export) and anything explicitly marked must go through.
  if (config?.responseType || config?.noCache) return rawGet(url, config);

  const key = keyOf(url, config);
  const hit = recent.get(key);
  if (hit && Date.now() - hit.at < FRESH_MS) {
    recordRequest('GET', url, 0, { cached: true });
    return Promise.resolve(hit.res);
  }

  const flying = inFlight.get(key);
  if (flying) {
    recordRequest('GET', url, 0, { cached: true, shared: true });
    return flying;
  }

  const startedAt = Date.now();
  const p = rawGet(url, config)
    .then((res) => {
      recordRequest('GET', url, Date.now() - startedAt, { serverMs: serverMsOf(res) });
      recent.set(key, { at: Date.now(), res });
      inFlight.delete(key);
      return res;
    })
    .catch((err) => {
      recordRequest('GET', url, Date.now() - startedAt, { failed: true, serverMs: serverMsOf(err?.response) });
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, p);
  return p;
};

/** Forget every cached read. Called whenever data changes. */
export function clearApiCache() {
  recent.clear();
  inFlight.clear();
}

// Any write invalidates every cached read, without each screen having to
// remember to do it. Adding a note, renaming a label, joining a group: all of
// them would otherwise risk showing the list as it was seconds earlier.
// Cleared BEFORE the request too, so a read fired while the write is in flight
// can't repopulate the cache with pre-write data.
['post', 'put', 'patch', 'delete'].forEach((method) => {
  const raw = apiClient[method].bind(apiClient);
  apiClient[method] = function writeThenForget(...args) {
    clearApiCache();
    const startedAt = Date.now();
    const url = args[0];
    return raw(...args).then(
      (res) => {
        recordRequest(method, url, Date.now() - startedAt, { serverMs: serverMsOf(res) });
        clearApiCache();
        return res;
      },
      (err) => {
        recordRequest(method, url, Date.now() - startedAt, { failed: true, serverMs: serverMsOf(err?.response) });
        clearApiCache();
        throw err;
      },
    );
  };
});

// On 401 (except for /auth/me which is the session check), redirect to login
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/auth/me')) {
      // Throw away the installed app's token on the way out. Without this a
      // dead one stays in storage and is offered on every single request until
      // somebody signs in again — a credential we already know is refused.
      setAppToken(null);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
